import { faceNodeGetFaceFromTable } from "building-generation/buildingGraph/faceNodeParent";
import { getDatumZ } from "building-generation/elements/datums";
import type { BuildingGraph, BuildingGraphPlane } from "building-types/building/buildingGraphTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import type { MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import { MasonryType } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { WallPlaneRuleHandle } from "building-types/handles/docEntityHandleTypes";
import { CMUProfileToNominalWidth } from "building-types/masonry/masonryTypes";
import type {
  WallPlaneOperationAABBVerticalSideValue,
  WallPlaneDomainOperation,
  WallPlaneDescriptor,
  WallPlane,
  WallPlaneSubdomain,
  WallPlaneSubdomainInfo,
  WallPlaneCoreDirection,
} from "building-types/walls2/wallPlane";
import {
  getWallPlaneOperationAABB,
  isWallPlaneRoofClippingDomainOperation,
  isWallPlaneStairClippingDomainOperation,
} from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity, WallPlaneAssemblyLayer } from "building-types/walls2/wallPlaneAssemblyTypes";
import { resolveWallPlaneOperationAABBToUVAABB } from "building-types/walls2/wallPlaneOperationAABB";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import type { Mutable } from "common/utils/types";
import { getFramingProfileDescriptor } from "doc/utils/lumberTools";
import { aabb2Init, aabb2AddAABB2, aabb2Grow, aabb2Copy, aabb2OverlapArea } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb2New } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { transformFaceLoopBasis } from "geom/brep";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { polyLine2PolyLine2DoIntersect } from "geom/intersect";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import type { Plane } from "geom/planeTypes";
import { polyLine2AABB2, polyLine2Copy, polyLine2FromAABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import RBush from "rbush";

type WallPlaneDomainBox = AABB2 & {
  operation: WallPlaneDomainOperation;
  polyline: PolyLine2;
  sortIndex: number;
};

export class WallPlaneDescriptorBuilder {
  private subdomainOperationCount = 0;
  private readonly subdomainOperationIndex = new RBush<WallPlaneDomainBox>();

  private readonly xCoordinates = new Set<number>();
  private readonly yCoordinates = new Set<number>();

  private readonly wallPlane: Plane;

  constructor(
    private readonly buildingGraph: BuildingGraph,
    private readonly buildingGraphPlane: BuildingGraphPlane,
    private readonly resolvedDatums: ResolvedDatumTable,
    private readonly datumAbstractions: Table<DatumAbstraction>,
  ) {
    // include the full plane aabb in the domain lattice
    const outerAabb = polyLine2AABB2(buildingGraphPlane.planeFace.loops[0]);
    this.xCoordinates.add(outerAabb.minX);
    this.yCoordinates.add(outerAabb.minY);
    this.xCoordinates.add(outerAabb.maxX);
    this.yCoordinates.add(outerAabb.maxY);

    this.wallPlane = buildingGraphPlane.planeFace.plane;
  }

  private operationBoundsHasSides(operation: WallPlaneDomainOperation): boolean {
    const operationAABB = getWallPlaneOperationAABB(operation.operationBoundary);
    return (
      operationAABB?.start !== undefined ||
      operationAABB?.end !== undefined ||
      operationAABB?.bottom !== undefined ||
      operationAABB?.top !== undefined
    );
  }

  private resolveOperationAbsoluteBoundary(sideValue: WallPlaneOperationAABBVerticalSideValue): number | undefined {
    if (sideValue.mode === "RELATIVE") {
      return sideValue.value;
    }
    const resolvedDatum = this.resolvedDatums[sideValue.datumId];
    if (!resolvedDatum) {
      return undefined;
    }
    return getDatumZ(resolvedDatum);
  }

  private resolveOperationDomainAABB(selectedAABB: AABB2, operation: WallPlaneDomainOperation): AABB2 | null {
    const boundedAABB = getWallPlaneOperationAABB(operation.operationBoundary);
    if (!boundedAABB) {
      return aabb2New(selectedAABB.minX, selectedAABB.minY, selectedAABB.maxX, selectedAABB.maxY);
    }

    return resolveWallPlaneOperationAABBToUVAABB(
      boundedAABB,
      this.buildingGraphPlane.planeHash,
      selectedAABB,
      this.datumAbstractions,
      this.buildingGraph,
      (datumId) => {
        const sideValue: WallPlaneOperationAABBVerticalSideValue = { mode: "DATUM", datumId };
        return this.resolveOperationAbsoluteBoundary(sideValue);
      },
    );
  }

  private getOperationDomainPolyline(polyline: PolyLine2, operation: WallPlaneDomainOperation): PolyLine2 | null {
    if (!this.operationBoundsHasSides(operation)) {
      return polyline;
    }
    const selectedAABB = polyLine2AABB2(polyline);
    const selectedDomainAABB = this.resolveOperationDomainAABB(selectedAABB, operation);
    if (!selectedDomainAABB) {
      return null;
    }
    return polyLine2FromAABB2(selectedDomainAABB);
  }

  private addPolylineDomainOperation(polyline: PolyLine2, operation: WallPlaneDomainOperation) {
    const domainPolyline = this.getOperationDomainPolyline(polyline, operation);
    if (!domainPolyline) {
      return;
    }
    const aabb2 = polyLine2AABB2(domainPolyline);
    this.subdomainOperationIndex.insert({
      ...aabb2,
      operation,
      polyline: domainPolyline,
      sortIndex: this.subdomainOperationCount++,
    });

    for (let i = 0; i < domainPolyline.length; i += 2) {
      this.xCoordinates.add(domainPolyline[i]);
      this.yCoordinates.add(domainPolyline[i + 1]);
    }
  }

  private addPlanarGraphFaceDomainOperation(
    planarGraphPlane: Plane,
    planarGraph: PlanarGraph,
    faceIndex: number,
    operation: WallPlaneDomainOperation,
  ) {
    const faceIndices = planarGraph.faceHalfEdges[faceIndex];
    if (!faceIndices) {
      return;
    }
    const faceLoop = planarGraphHalfEdgeStripToPolyLine2(planarGraph, faceIndices);
    const pl2 = transformFaceLoopBasis(faceLoop, planarGraphPlane, this.wallPlane);

    this.addPolylineDomainOperation(pl2, operation);
  }

  private addFaceDomainOperation(face: Face, operation: WallPlaneDomainOperation) {
    const { plane, loops } = face;

    // transformFaceLoopBasis mutates its input, so copy to avoid mutating the face
    const faceLoop = polyLine2Copy(loops[0]);
    const pl2 = transformFaceLoopBasis(faceLoop, plane, this.wallPlane);

    this.addPolylineDomainOperation(pl2, operation);
  }

  public addOperation(operation: WallPlaneDomainOperation) {
    const { buildingGraph } = this;
    const { faceEdges, faceNodes, faceTopologies, volumeNodes } = buildingGraph;
    const handle = operation.wallPlaneFaceSideHandle;
    switch (handle.type) {
      case "WALL_PLANE_SIDE": {
        // Whole-plane domain: AABB of the plane outline.
        const planeAABB = polyLine2AABB2(this.buildingGraphPlane.planeFace.loops[0]);
        const pl2 = polyLine2FromAABB2(planeAABB);
        this.addPolylineDomainOperation(pl2, operation);
        break;
      }
      case "TOPOLOGY_FACE_SIDE": {
        const { topologyId, faceIndex } = handle.faceSource;
        const topology = faceTopologies[topologyId];
        assertIsNotNullOrUndefined(topology);
        const { plane, planeTopology } = topology;
        const { planarGraph } = planeTopology;
        this.addPlanarGraphFaceDomainOperation(plane, planarGraph, faceIndex, operation);
        break;
      }
      case "FACE_EDGE_SIDE": {
        const { faceEdgeIndex } = handle.faceSource;
        const faceEdge = faceEdges[faceEdgeIndex];
        assertIsNotNullOrUndefined(faceEdge);
        const { face } = faceEdge;
        if (!face) {
          return;
        }
        this.addFaceDomainOperation(face, operation);
        break;
      }
      case "FACE_NODE_SIDE": {
        const { faceNodeId } = handle.faceSource;
        const faceNode = faceNodes[faceNodeId];
        assertIsNotNullOrUndefined(faceNode);
        const face = faceNodeGetFaceFromTable(faceNode, volumeNodes);
        assertIsNotNullOrUndefined(face);
        this.addFaceDomainOperation(face, operation);
        break;
      }
      default: {
        assertUnreachable(handle);
      }
    }
  }

  public build(
    wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
    framingComponents: Table<FramingComponentEntity>,
    masonryComponents: Table<MasonryComponentEntity>,
    cipComponents: Table<CipComponentEntity>,
    sheathingComponents: Table<SheathingComponentEntity>,
  ): WallPlaneDescriptor {
    const subdomainsSortedXCoordinates = Array.from(this.xCoordinates).sort((a, b) => a - b);
    const subdomainsSortedYCoordinates = Array.from(this.yCoordinates).sort((a, b) => a - b);

    let subdomainMinW = Infinity;
    let subdomainMaxW = -Infinity;

    const updateSubdomainMinMaxW = (wallInfo: WallPlaneSubdomainInfo) => {
      subdomainMinW = Math.min(subdomainMinW, -wallInfo.wallThickness / 2 - wallInfo.wallOffset);
      subdomainMaxW = Math.max(subdomainMaxW, wallInfo.wallThickness / 2 + wallInfo.wallOffset);
    };

    const subdomainAABB = aabb2Init();
    const subdomains: WallPlaneSubdomain[][] = [];
    const subdomainIndex = new RBush<WallPlaneSubdomain>();

    for (let yi = 0; yi < subdomainsSortedYCoordinates.length - 1; yi++) {
      const minY = subdomainsSortedYCoordinates[yi];
      const maxY = subdomainsSortedYCoordinates[yi + 1];

      // TODO - maybe need this, but it will mess with the array indexing
      // if (maxY - minY <= DIST_EPSILON) {
      //   continue;
      // }

      const arr: WallPlaneSubdomain[] = [];
      subdomains.push(arr);

      for (let xi = 0; xi < subdomainsSortedXCoordinates.length - 1; xi++) {
        const minX = subdomainsSortedXCoordinates[xi];
        const maxX = subdomainsSortedXCoordinates[xi + 1];

        // maybe need this, but it will mess with the array indexing
        // if (maxY - minY <= DIST_EPSILON) {
        //   continue;
        // }

        const sumdomainAABB2 = aabb2New(minX, minY, maxX, maxY);
        aabb2AddAABB2(subdomainAABB, sumdomainAABB2);

        // we want exclusive overlap, so we use a negative epsilon
        const searchEpsilon = -DIST_EPSILON;
        const searchAABB2 = aabb2Grow(aabb2Copy(sumdomainAABB2), searchEpsilon, searchEpsilon);

        const subdomainCandidates = this.subdomainOperationIndex.search(searchAABB2);
        const subdomainResults: WallPlaneDomainBox[] = [];
        for (let i = 0; i < subdomainCandidates.length; i++) {
          const candidate = subdomainCandidates[i];
          if (domainPolylineOverlapsSearchAABB2(searchAABB2, candidate.polyline)) {
            subdomainResults.push(candidate);
          }
        }

        // restore the original order of the operations
        // TODO - maybe best stored as a reference to the original doc operation?
        subdomainResults.sort((a, b) => a.sortIndex - b.sortIndex);
        const allSubdomainOperations = subdomainResults.map((result) => result.operation);
        const subdomainOperations = allSubdomainOperations.filter(
          (op) => !isWallPlaneRoofClippingDomainOperation(op) && !isWallPlaneStairClippingDomainOperation(op),
        );
        const subdomainRoofClippingOperations = allSubdomainOperations.filter((op) =>
          isWallPlaneRoofClippingDomainOperation(op),
        );
        const subdomainStairClippingOperations = allSubdomainOperations.filter((op) =>
          isWallPlaneStairClippingDomainOperation(op),
        );

        const wallInfo = computeWallPlaneSubdomainInfo(
          subdomainOperations,
          wallPlaneAssemblies,
          framingComponents,
          masonryComponents,
          cipComponents,
          sheathingComponents,
        );

        if (wallInfo) {
          updateSubdomainMinMaxW(wallInfo);
        }

        const faceNodeIds: string[] = [];
        const faceEdgeIds: number[] = [];
        const topologyIds: string[] = [];

        const topologyOverlapAreas: { topologyId: string; overlapArea: number }[] = [];

        // include info for this subdomain from the building graph topology
        for (const topologyId of this.buildingGraphPlane.planeTopologyIds) {
          const topology = this.buildingGraph.faceTopologies[topologyId];

          let includeTopology = false;
          let overlapArea = 0;

          const topologyFaceIdIndexResults = topology.faceNodePlaneRTree.search(searchAABB2);
          for (const faceIdIndexResult of topologyFaceIdIndexResults) {
            const faceId = faceIdIndexResult.faceNodeId;
            faceNodeIds.push(faceId);
            includeTopology = true;
            overlapArea += aabb2OverlapArea(searchAABB2, faceIdIndexResult);
          }

          const topologyFaceEdgeIdIndexResults = topology.faceEdgePlaneRTree.search(searchAABB2);
          for (const faceEdgeIdIndexResult of topologyFaceEdgeIdIndexResults) {
            const faceEdgeId = faceEdgeIdIndexResult.faceEdgeIndex;
            faceEdgeIds.push(faceEdgeId);
            includeTopology = true;
            overlapArea += aabb2OverlapArea(searchAABB2, faceEdgeIdIndexResult);
          }

          if (includeTopology) {
            topologyOverlapAreas.push({ topologyId, overlapArea });
          }
        }

        topologyOverlapAreas.sort(
          (a, b) => b.overlapArea - a.overlapArea || a.topologyId.localeCompare(b.topologyId, "en"),
        );
        for (const { topologyId } of topologyOverlapAreas) {
          topologyIds.push(topologyId);
        }

        const wallCoreSubdomain: WallPlaneSubdomain = {
          id: `${this.buildingGraphPlane.planeHash}.${xi}.${yi}`,
          ...sumdomainAABB2,
          operations: subdomainOperations,
          roofClippingOperations: subdomainRoofClippingOperations,
          stairClippingOperations: subdomainStairClippingOperations,
          wallInfo,
          arrayIndexU: xi,
          arrayIndexV: yi,
          topologyIds,
          faceNodeIds,
          faceEdgeIds,
        };

        arr.push(wallCoreSubdomain);
        subdomainIndex.insert(wallCoreSubdomain);
      }
    }

    // 2. Apply non wrapped cladding operations to the subdomains

    const subdomainCladdingOperations = this.subdomainOperationIndex
      .all()
      .filter((op) => op.operation.wallPlaneOperator.operationType === "SET_WALL_FINISH")
      .sort((a, b) => a.sortIndex - b.sortIndex);

    for (const op of subdomainCladdingOperations) {
      const operation = op.operation;
      if (operation.wallPlaneOperator.operationType !== "SET_WALL_FINISH") {
        throw new Error("Expected SET_WALL_FINISH operation");
      }

      /**
       * @TODO WALLS V2 - consider letting either the rule or the assembly define
       * whether or not it should be treated as solid or wrap.
       * If wrap, it would continue here
       */

      // get all relevant subdomains
      const operationPolyline = op.polyline;
      const operationAABB2 = aabb2New(op.minX, op.minY, op.maxX, op.maxY);
      const subdomainCandidatesRaw = subdomainIndex.search(operationAABB2);
      const subdomainCandidates: WallPlaneSubdomain[] = [];
      for (let i = 0; i < subdomainCandidatesRaw.length; i++) {
        const subdomain = subdomainCandidatesRaw[i];
        const searchAABB2 = aabb2Grow(aabb2Copy(subdomain), -DIST_EPSILON, -DIST_EPSILON);
        if (domainPolylineOverlapsSearchAABB2(searchAABB2, operationPolyline)) {
          subdomainCandidates.push(subdomain);
        }
      }

      // Anchor cladding to the max core offset across every subdomain that shares this
      // cladding source on the plane — not only this finish op's UV domain. Face-scoped
      // finish ops (e.g. room∩floor floor-band faces) would otherwise never see a solid
      // core above/below and would leave outward cladding inset on the wall centerline.
      let maxLeftCoreOffsetForSubdomains: number | undefined = undefined;
      let maxRightCoreOffsetForSubdomains: number | undefined = undefined;
      for (const row of subdomains) {
        for (const subdomain of row) {
          if (!subdomain.wallInfo) {
            continue;
          }
          const { wallInfo } = subdomain;
          const { coreThickness, wallOffset, leftCladdingSource, rightCladdingSource } = wallInfo;

          // left attachment point found
          if (leftCladdingSource === op.operation.source) {
            const leftCoreOffset = coreThickness / 2 - wallOffset;
            maxLeftCoreOffsetForSubdomains =
              maxLeftCoreOffsetForSubdomains === undefined
                ? leftCoreOffset
                : Math.max(maxLeftCoreOffsetForSubdomains, leftCoreOffset);
          }

          // right attachment point found
          if (rightCladdingSource === op.operation.source) {
            const rightCoreOffset = coreThickness / 2 + wallOffset;
            maxRightCoreOffsetForSubdomains =
              maxRightCoreOffsetForSubdomains === undefined
                ? rightCoreOffset
                : Math.max(maxRightCoreOffsetForSubdomains, rightCoreOffset);
          }
        }
      }

      if (maxLeftCoreOffsetForSubdomains === undefined && maxRightCoreOffsetForSubdomains === undefined) {
        continue; // this cladding operation did not resolve on the wall
      }

      for (const subdomain of subdomainCandidates) {
        if (!subdomain.wallInfo) {
          continue;
        }
        /**
         * This is a safe cast inside this generation process. We want to
         * keep the returned value immutable though
         */
        const wallInfo = subdomain.wallInfo as Mutable<WallPlaneSubdomainInfo>;
        const { coreThickness, wallOffset, leftCladdingSource, rightCladdingSource } = wallInfo;
        if (maxLeftCoreOffsetForSubdomains !== undefined && leftCladdingSource === op.operation.source) {
          wallInfo.leftCoreToCladdingAirGap = maxLeftCoreOffsetForSubdomains - coreThickness / 2 + wallOffset;
          wallInfo.wallThickness += wallInfo.leftCoreToCladdingAirGap;

          updateSubdomainMinMaxW(wallInfo);
        }
        if (maxRightCoreOffsetForSubdomains !== undefined && rightCladdingSource === op.operation.source) {
          wallInfo.rightCoreToCladdingAirGap = maxRightCoreOffsetForSubdomains - coreThickness / 2 - wallOffset;
          wallInfo.wallThickness += wallInfo.rightCoreToCladdingAirGap;

          updateSubdomainMinMaxW(wallInfo);
        }
      }
    }

    return {
      subdomainMinW,
      subdomainMaxW,
      subdomainAABB,
      subdomains,
      subdomainIndex,
    };
  }
}

// discard polyline for the search box
const searchPolyline = polyLine2FromAABB2(aabb2New(0, 0, 0, 0));

function domainPolylineOverlapsSearchAABB2(searchAABB2: AABB2, domainPolyline: PolyLine2): boolean {
  const { minX: searchMinX, maxX: searchMaxX, minY: searchMinY, maxY: searchMaxY } = searchAABB2;

  if (
    classifyContainmentVec2PolyLine2Fast(searchMinX, searchMinY, domainPolyline) ||
    classifyContainmentVec2PolyLine2Fast(searchMaxX, searchMinY, domainPolyline) ||
    classifyContainmentVec2PolyLine2Fast(searchMaxX, searchMaxY, domainPolyline) ||
    classifyContainmentVec2PolyLine2Fast(searchMinX, searchMaxY, domainPolyline)
  ) {
    return true;
  }

  // This function is called a lot. rather than re-allocating the float array each time,
  // we can just reuse the same array.

  searchPolyline[0] = searchMinX;
  searchPolyline[1] = searchMinY;
  searchPolyline[2] = searchMaxX;
  searchPolyline[3] = searchMinY;
  searchPolyline[4] = searchMaxX;
  searchPolyline[5] = searchMaxY;
  searchPolyline[6] = searchMinX;
  searchPolyline[7] = searchMaxY;
  searchPolyline[8] = searchMinX;
  searchPolyline[9] = searchMinY;

  return polyLine2PolyLine2DoIntersect(searchPolyline, domainPolyline);
}

export function computeWallPlaneSubdomainInfo(
  wallCoreOperations: WallPlaneDomainOperation[],
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): WallPlaneSubdomainInfo | null {
  let wallOffset = 0;

  let coreSource: WallPlaneRuleHandle | undefined;
  let coreAssembly: WallPlaneAssemblyEntity | null = null;
  let coreDirection: WallPlaneCoreDirection = "LEFT_TO_RIGHT";

  let leftCladdingSource: WallPlaneRuleHandle | undefined;
  let leftCladdingAssembly: WallPlaneAssemblyEntity | null = null;

  let rightCladdingSource: WallPlaneRuleHandle | undefined;
  let rightCladdingAssembly: WallPlaneAssemblyEntity | null = null;

  for (const operation of wallCoreOperations) {
    const { wallPlaneOperator, resolvedFaceSide } = operation;
    switch (wallPlaneOperator.operationType) {
      case "SET_WALL_OFFSET": {
        wallOffset = wallPlaneOperator.offset;
        if (resolvedFaceSide === "LEFT") {
          wallOffset = -wallOffset;
        }
        break;
      }
      case "SET_WALL_CORE": {
        const { assemblyId } = wallPlaneOperator;
        const assembly = assemblyId ? wallPlaneAssemblies[assemblyId] : null;
        coreAssembly = assembly;
        coreSource = operation.source;
        if (resolvedFaceSide === "RIGHT") {
          coreDirection = "RIGHT_TO_LEFT";
        }
        break;
      }
      case "SET_WALL_FINISH": {
        const { assemblyId } = wallPlaneOperator;
        const assembly = assemblyId ? wallPlaneAssemblies[assemblyId] : null;
        // In generation, `resolvedFaceSide` is computed from the same building graph
        // that produced this handle and cached alongside it; undefined here indicates
        // stale/invalid precomputed data and should fail loudly.
        assertIsNotNullOrUndefined(resolvedFaceSide);
        if (resolvedFaceSide === "LEFT") {
          leftCladdingAssembly = assembly;
          leftCladdingSource = operation.source;
        } else {
          rightCladdingAssembly = assembly;
          rightCladdingSource = operation.source;
        }
        break;
      }
      case "SET_WALL_CUT": {
        // void subdomain
        return null;
      }
      default: {
        assertUnreachable(wallPlaneOperator);
      }
    }
  }

  // void subdomain
  if (!coreAssembly && !leftCladdingAssembly && !rightCladdingAssembly) {
    return null;
  }

  const coreThickness = accumulateWallPlaneAssemblyThickness(
    coreAssembly,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  );

  const leftCladdingThickness = accumulateWallPlaneAssemblyThickness(
    leftCladdingAssembly,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  );
  const rightCladdingThickness = accumulateWallPlaneAssemblyThickness(
    rightCladdingAssembly,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  );

  const wallThickness = coreThickness + leftCladdingThickness + rightCladdingThickness;

  return {
    wallThickness,
    wallOffset,

    coreSource,
    coreAssembly,
    coreThickness,
    coreDirection,

    leftCladdingSource,
    leftCladdingAssembly,
    leftCladdingThickness,
    leftCoreToCladdingAirGap: 0,

    rightCladdingSource,
    rightCladdingAssembly,
    rightCladdingThickness,
    rightCoreToCladdingAirGap: 0,
  };
}

export function accumulateWallPlaneAssemblyThickness(
  wallPlaneAssembly: WallPlaneAssemblyEntity | null,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): number {
  let thickness = 0;
  if (!wallPlaneAssembly) {
    return thickness;
  }
  for (const layer of sortTableBySortIndex(wallPlaneAssembly.layers)) {
    thickness += getWallPlaneAssemblyLayerThickness(
      layer,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
    );
  }
  return thickness;
}

export function getWallPlaneAssemblyLayerThickness(
  layer: WallPlaneAssemblyLayer,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): number {
  switch (layer.layerType) {
    case "FRAMING": {
      const framingComponent = framingComponents[layer.framingComponentId];
      assertIsNotNullOrUndefined(framingComponent);
      return getFramingProfileDescriptor(framingComponent).width;
    }
    case "MASONRY": {
      const masonryComponent = masonryComponents[layer.masonryComponentId];
      assertIsNotNullOrUndefined(masonryComponent);
      if (masonryComponent.masonryType === MasonryType.CONCRETE) {
        return CMUProfileToNominalWidth[masonryComponent.profile];
      } else {
        return masonryComponent.width;
      }
    }
    case "CAST_IN_PLACE": {
      const cipComponent = cipComponents[layer.cipComponentId];
      assertIsNotNullOrUndefined(cipComponent);
      return cipComponent.thickness;
    }
    case "SHEATHING": {
      const sheathingComponent = sheathingComponents[layer.sheathingComponentId];
      assertIsNotNullOrUndefined(sheathingComponent);
      return sheathingComponent.thickness;
    }
    default: {
      assertUnreachable(layer);
    }
  }
}

export function rebuildWallPlaneDescriptorWithAdditionalOperations(
  wallPlane: WallPlane,
  buildingGraph: BuildingGraph,
  additionalOperations: readonly WallPlaneDomainOperation[],
  resolvedDatums: ResolvedDatumTable,
  datumAbstractions: Table<DatumAbstraction>,
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
): WallPlaneDescriptor {
  if (additionalOperations.length === 0) {
    return wallPlane.descriptor;
  }

  const buildingPlane = buildingGraph.buildingPlanes[wallPlane.planeHash];
  assertIsNotNullOrUndefined(buildingPlane);

  const wallPlaneBuilder = new WallPlaneDescriptorBuilder(
    buildingGraph,
    buildingPlane,
    resolvedDatums,
    datumAbstractions,
  );
  for (const operation of wallPlane.operations) {
    wallPlaneBuilder.addOperation(operation);
  }
  for (const operation of additionalOperations) {
    wallPlaneBuilder.addOperation(operation);
  }

  return wallPlaneBuilder.build(
    wallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
  );
}
